import { Search, Menu, X } from 'lucide-react'
import { Button, SlotTrigger, shortcut, shortcutLabel, useShellCompact } from '@solum/ui'
import { useSolum } from '../data/client'
import type { RefObject } from 'react'

import { NotificationCenter } from './NotificationCenter'
import { RouteChromeBar } from './RouteChromeBar'
import { InstallButton } from './InstallButton'
import { RecentPinnedTrigger } from './RecentPinnedTrigger'
import { ConsoleUserMenu } from './ConsoleUserMenu'
import s from './Topbar.module.css'

/**
 * Three zones, and the order is load-bearing (#1466 slice A).
 *
 *   DEPLOYMENT — hamburger, DEMO DATA
 *   PRODUCT    — the command palette, install, notifications
 *   ACCOUNT    — one control: the shared user menu

 * ── TWO OF THOSE MOVED TO THE NAVIGATION COLUMN (#1991, ruling 72563) ───────
 * §1 puts *"Moonbase mark, product/host name and environment/status"* in one
 * deliberate header, and §7 puts the signed-in user in a footer that does not
 * scroll away. `ConsoleNavigationShell` is that header and that footer, and it
 * argues each move; what stays here is what a phone must reach without opening
 * the drawer first — the account menu, so signing out is not behind a menu
 * inside a menu. The lockup travels with the navigation pane.
 *
 * This bar used to be ELEVEN children in one flat flex row with the four account
 * concerns non-adjacent, split apart by two product controls. Personalize, the
 * theme glyph, the signed-in email and sign-out are now inside the account menu,
 * because each is a fact about — or a preference of — the PERSON.
 *
 * What deliberately stays a product control, and why each is not an account one:
 * the palette acts on the product's CONTENT, not on you (and it owns the first
 * `<kbd>` in the document, which UX-09 resolves); a notification count must be
 * legible WITHOUT opening a disclosure, which is the whole of "at a glance";
 * install is a capability of the browser and the device, and it disappears once
 * taken. The DEMO DATA badge and environment health are statements about the
 * DEPLOYMENT.
 *
 * Net width: three shared `Button`s and an 18ch email span leave, one 32px
 * avatar arrives — the row sheds roughly 150px at every viewport.
 *
 * ── #2328 §1 — AND THE ACCOUNT CAME BACK, AT EVERY WIDTH ───────────────────
 * The `account` prop is gone. It existed because ruling 72563 §7 put the
 * signed-in user in the NAVIGATION FOOTER on desktop and here only where that
 * column is a closed drawer, so the shell had one menu whose PLACEMENT moved
 * with the viewport. #2328 §1 supersedes that: the header carries global search,
 * the Recent & Pinned trigger and the signed-in user / active persona, and it
 * *"remains stable across Console pages"* — a control that moves between two
 * landmarks by width is not stable. There is still exactly ONE mounted menu;
 * what changed is that its home no longer depends on the viewport.
 *
 * The governed order §1 names is search → Recent & Pinned → signed-in user.
 * This bar has five right-hand controls, not three: install and notifications
 * sit between the second and the third, keeping the three named ones in their
 * ruled relative order. Each is omitted when it has nothing to offer — install
 * renders nothing where the browser cannot install, notifications nothing
 * without a session — and omission leaves no gap, because these are flex items
 * with no reserved width.
 */
export function Topbar({ onOpenCommand, onToggleNav, navOpen, onPersonalizeNav, onConnectedApps, accountTrigger, inert }: {
  onOpenCommand: () => void; onToggleNav?: () => void; navOpen?: boolean
  /* #1991/#2267 — this is the explicit navigation action. Contextual table and
   * form customization lives on those surfaces and cannot intercept it. */
  onPersonalizeNav?: () => void
  /** Opens Connected apps (#2443 slice 4); absent ⇒ the menu offers no such row. */
  onConnectedApps?: () => void
  /* The element focus returns to when edit mode closes. The launching menu item
   * is unmounted by then; the trigger is what survives (#1466 F-30B). */
  accountTrigger?: RefObject<HTMLButtonElement | null>
  /* The Settings Studio makes the shell behind it unreachable (#2328 5). A
     prop rather than a global: `inert` may only be set by an ancestor, and the
     shell is this header's ancestor -- so the Studio asks AppShell and AppShell
     asks here. React 19 types it as a boolean and omits the attribute for
     false, which matters because `inert="false"` is still inert. */
  inert?: boolean
}) {
  const { isLive } = useSolum()
  /* The shell's own compact boundary (860px, `breakpoints.ts`) — the width
     at which the search gives up its words for its glyph. */
  const compact = useShellCompact()
  return (
    /* `inert` is the Settings Studio making the shell behind it unreachable
       (#2328 5). ONE attribute for both halves of that sentence -- out of the
       accessibility tree AND unfocusable -- rather than `aria-hidden` beside a
       focus trap, which are two mechanisms that drift apart. The value is the
       empty string when on and the prop is absent when off, because
       `inert="false"` is still inert. */
    <header className={s.top} inert={inert} data-testid="app-header">
      {onToggleNav && (
        /* The kit's ghost Button, not a hand-drawn one (#2406 rogue census,
           app-styled-control): `.hamburger` is visibility and placement only —
           the paint, the hover and the focus ring are the Button's. */
        <Button variant="ghost" size="sm" className={s.hamburger} onClick={onToggleNav}
          aria-label={navOpen ? 'Close menu' : 'Open menu'} data-testid="nav-toggle"
          icon={navOpen ? <X size={18} /> : <Menu size={18} />} />
      )}
      {/* Back and the path — the route declares them, this draws them. A route
          that declares none renders nothing and costs no space. */}
      <RouteChromeBar />
      {!isLive && <span className={s.badge}>DEMO DATA</span>}
      <div className={s.sp} />
      {/* The kit's slot-shaped trigger (#2406 b3-shell): it OPENS the palette
          and holds no value, so it is drawn as the slot it stands for. On the
          compact shell it is its glyph alone. The kbd is decorative: a screen
          reader says "place of interest sign" for ⌘, so the accessible name
          carries the shortcut in words. */}
      <SlotTrigger onClick={onOpenCommand} iconOnly={compact}
        icon={<Search size={14} />} shortcut={shortcut('K')}
        aria-label={`Open command palette, ${shortcutLabel('K')}`}>
        Search
      </SlotTrigger>
      {/* §1 item 2 — one trigger for the Recent & Pinned lens, in the header. */}
      <RecentPinnedTrigger />
      <div className={s.product}>
        <InstallButton />
        <NotificationCenter />
      </div>
      {/* §1 item 3 — the signed-in user AND the active persona, one control. */}
      <div className={s.account}>
        <ConsoleUserMenu triggerRef={accountTrigger} onPersonalizeNav={onPersonalizeNav} onConnectedApps={onConnectedApps} />
      </div>
    </header>
  )
}
